import { useQuery } from "@tanstack/react-query";
import { IcoMenu, IcoPlus, IcoSearch } from "./icons";
import { NotificationBell } from "./NotificationBell";
import { recomputeDays } from "../lib/recomputeDays";
import { launchAvatarText } from "../lib/launchAvatar";
import { tasksApi } from "../api/endpoints";
import type { Launch, LaunchStatus, Stage, Task } from "../types/domain";

const STATUS_META: Record<
  LaunchStatus,
  { label: string; dotColor: string; pulse?: boolean }
> = {
  draft: { label: "Черновик", dotColor: "#A0A8BD" },
  active: { label: "Активный", dotColor: "#14B8A6", pulse: true },
  completed: { label: "Завершён", dotColor: "#6366F1" },
  archived: { label: "В архиве", dotColor: "#9CA3AF" },
};

const STAGE_SHORT: Record<Stage, string> = {
  warmup: "Прогрев",
  sales: "Эфир",
  close: "Дожим",
};

const pluralDays = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "дня";
  return "дней";
};

function launchProgressText(launch: Launch): string {
  if (!launch.days?.length) return STATUS_META[launch.status].label;
  const labelled = recomputeDays(launch.days, launch.d1StartDate);
  if (!labelled.length) return STATUS_META[launch.status].label;

  const todayIdx = labelled.findIndex((d) => d.isToday);
  if (todayIdx >= 0) {
    const day = labelled[todayIdx]!;
    return `День ${todayIdx + 1} из ${labelled.length} · ${STAGE_SHORT[day.stage]}`;
  }

  const first = labelled[0]!;
  const last = labelled[labelled.length - 1]!;
  const todayMs = new Date(`${todayIsoLocal()}T00:00:00`).getTime();
  const firstMs = new Date(`${first.isoDate}T00:00:00`).getTime();
  const lastMs = new Date(`${last.isoDate}T00:00:00`).getTime();
  const day = 24 * 60 * 60 * 1000;

  if (todayMs < firstMs) {
    const diff = Math.round((firstMs - todayMs) / day);
    if (diff === 1) return "Старт завтра";
    return `До старта ${diff} ${pluralDays(diff)}`;
  }
  const diff = Math.round((todayMs - lastMs) / day);
  if (diff === 0) return "Завершается сегодня";
  if (diff === 1) return "Завершился вчера";
  return `Завершился ${diff} ${pluralDays(diff)} назад`;
}

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type TopbarMode = "launches" | "landings" | "templates" | "tags" | "board" | "tasks";

interface TopbarProps {
  mode: TopbarMode;
  launches?: Launch[];
  activeLaunchId?: string | null;
  canViewTasks?: boolean;
  onAllLaunches?: () => void;
  onAddCard?: () => void;
  onMobileMenu?: () => void;
}

const SECTION_LABELS: Record<Exclude<TopbarMode, "board">, string> = {
  launches: "Запуски",
  landings: "Лендинги",
  templates: "Шаблоны писем",
  tags: "Типы карточек",
  tasks: "Задачи",
};

export function Topbar({
  mode,
  launches,
  activeLaunchId,
  canViewTasks = false,
  onAllLaunches,
  onAddCard,
  onMobileMenu,
}: TopbarProps) {
  const menuBtn = onMobileMenu && (
    <button
      type="button"
      className="topbar-mobile-menu"
      onClick={onMobileMenu}
      aria-label="Открыть меню"
    >
      <IcoMenu size={20} />
    </button>
  );

  if (mode !== "board") {
    return (
      <div className="topbar">
        {menuBtn}
        <div className="topbar-launch">
          <div className="breadcrumbs">
            <span className="crumb-current">{SECTION_LABELS[mode]}</span>
          </div>
          <div className="topbar-mobile-title">{SECTION_LABELS[mode]}</div>
        </div>
        <div className="topbar-right">
          <NotificationBell />
          <div className="search">
            <IcoSearch size={15} stroke="#7A849B" />
            <input placeholder="Поиск по всему..." />
            <span className="kbd">⌘K</span>
          </div>
        </div>
      </div>
    );
  }

  const all = launches ?? [];
  const active = all.find((l) => l.id === activeLaunchId) ?? null;

  return (
    <div className="topbar">
      {menuBtn}
      <div className="topbar-launch">
        {active && <LaunchProgressChip launch={active} canViewTasks={canViewTasks} />}
        <div className="breadcrumbs">
          <button type="button" className="crumb-link" onClick={onAllLaunches}>
            Запуски
          </button>
          <span className="sep">/</span>
          <span className="crumb-mid">{active?.name ?? "—"}</span>
          <span className="sep">/</span>
          <span className="crumb-current">Доска</span>
        </div>
      </div>

      <div className="topbar-right">
        <NotificationBell />
        <div className="search">
          <IcoSearch size={15} stroke="#7A849B" />
          <input placeholder="Поиск карточки..." />
          <span className="kbd">⌘K</span>
        </div>
        {onAddCard && (
          <button
            type="button"
            className="btn-ghost topbar-add-card"
            onClick={onAddCard}
            aria-label="Новая карточка"
          >
            <IcoPlus size={14} /> <span className="topbar-add-card-label">Карточка</span>
          </button>
        )}
      </div>
    </div>
  );
}

function LaunchProgressChip({
  launch,
  canViewTasks,
}: {
  launch: Launch;
  canViewTasks: boolean;
}) {
  const status = STATUS_META[launch.status];
  const progress = launchProgressText(launch);

  // Прогресс задач этого запуска — отдельный лёгкий запрос, кэш 30 сек.
  // SSE по задачам инвалидирует ["tasks"] и обновит чип в реальном времени.
  const tasksQuery = useQuery({
    queryKey: ["tasks", "launch", launch.id],
    queryFn: async () => (await tasksApi.list({ launchId: launch.id })).tasks,
    enabled: canViewTasks,
    staleTime: 30_000,
  });
  const taskStats = computeTaskStats(tasksQuery.data ?? null);

  return (
    <div className="launch-chip">
      <div className="launch-dot" style={{ background: launch.color ?? "var(--accent)" }}>
        {launchAvatarText(launch)}
      </div>
      <div className="launch-chip-meta">
        <b>{launch.name}</b>
        <span className="launch-chip-progress">
          <span
            className="status-dot"
            style={{
              background: status.dotColor,
              animation: status.pulse ? "livePulse 1.8s ease-in-out infinite" : "none",
            }}
          />
          {progress}
        </span>
        {taskStats && (
          <span className="launch-chip-tasks" title="Задачи этого запуска">
            Задачи: <b>{taskStats.done}</b>/<b>{taskStats.total}</b>
            {taskStats.overdue > 0 && (
              <span className="launch-chip-tasks-overdue">
                {" "}· ⚠ {taskStats.overdue}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function computeTaskStats(
  tasks: Task[] | null,
): { total: number; done: number; overdue: number } | null {
  if (!tasks || tasks.length === 0) return null;
  const todayIso = todayIsoLocal();
  let done = 0;
  let overdue = 0;
  for (const t of tasks) {
    if (t.status === "done") done += 1;
    if (
      t.dueDate &&
      t.status !== "done" &&
      t.status !== "cancelled" &&
      t.dueDate < todayIso
    ) {
      overdue += 1;
    }
  }
  return { total: tasks.length, done, overdue };
}
